import React, { useState } from 'react';
import { uploadImageToServer } from '../utils/imageUpload';

export default function WrongAnswerNote() {
  const [selectedLevel, setSelectedLevel] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('');
  const [capturedImage, setCapturedImage] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [error, setError] = useState(null);

  const levels = ['중학생', '고등학생', '대학생', '일반인'];
  const subjects = {
    '중학생': ['수학', '영어', '국어', '과학', '사회'],
    '고등학생': ['수학', '영어', '국어', '과학', '사회', '한국사'],
    '대학생': ['수학', '영어', '전공', '교양'],
    '일반인': ['외국어', '자격증', '기타']
  };

  // 파일 선택 핸들러
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onload = (event) => {
        setCapturedImage(event.target.result);
      };
      reader.readAsDataURL(file);
      setError(null);
    }
  };

  // 분석 시작
  const handleAnalyze = async () => {
    if (!selectedLevel || !selectedSubject || !imageFile) {
      setError('레벨, 과목, 이미지를 모두 선택해주세요.');
      return;
    }

    setAnalyzing(true);
    setError(null);

    try {
      // 1. 이미지 업로드 (R2)
      const imageUrl = await uploadImageToServer(imageFile, {
        resize: true,
        maxDimension: 2048,
        ttl: 86400 * 30  // 30일
      });

      // 2. AI 분석 요청
      const formData = new FormData();
      formData.append('image', imageFile);
      formData.append('level', selectedLevel);
      formData.append('subject', selectedSubject);

      const response = await fetch('/api/learning/analyze', {
        method: 'POST',
        body: formData
      });

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || '분석 실패');
      }

      // 3. 분석 결과 저장
      setAnalysisResult({
        ...result.data,
        imageUrl,
        level: selectedLevel,
        subject: selectedSubject
      });

    } catch (err) {
      console.error('Analysis error:', err);
      setError(err.message || '분석 중 오류가 발생했습니다.');
    } finally {
      setAnalyzing(false);
    }
  };

  // 오답노트 저장
  const handleSave = async () => {
    if (!analysisResult) return;

    try {
      // 임시 user_id (나중에 Google OAuth로 교체)
      const user_id = 'test_user_1';

      const response = await fetch('/api/learning/records', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          user_id,
          level: analysisResult.level,
          subject: analysisResult.subject,
          image_url: analysisResult.imageUrl,
          problem_text: analysisResult.problem_text,
          student_answer: analysisResult.student_answer,
          correct_answer: analysisResult.correct_answer,
          explanation: analysisResult.explanation,
          key_concept: analysisResult.key_concept,
          difficulty: analysisResult.difficulty
        })
      });

      const result = await response.json();

      if (result.success) {
        alert('오답노트에 저장되었습니다!');
        // 초기화
        setCapturedImage(null);
        setImageFile(null);
        setAnalysisResult(null);
      } else {
        throw new Error(result.error);
      }

    } catch (err) {
      console.error('Save error:', err);
      alert('저장 실패: ' + err.message);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: '2rem',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    }}>
      <div style={{
        maxWidth: '800px',
        margin: '0 auto',
        background: 'white',
        borderRadius: '1rem',
        padding: '2rem',
        boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
      }}>
        <h1 style={{ fontSize: '2rem', marginBottom: '2rem', color: '#333' }}>
          📸 오답노트 등록
        </h1>

        {/* Step 1: 레벨 선택 */}
        <div style={{ marginBottom: '2rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
            학생 레벨:
          </label>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            {levels.map(level => (
              <button
                key={level}
                onClick={() => {
                  setSelectedLevel(level);
                  setSelectedSubject('');  // 레벨 변경 시 과목 초기화
                }}
                style={{
                  padding: '0.75rem 1.5rem',
                  border: selectedLevel === level ? '2px solid #667eea' : '2px solid #ddd',
                  borderRadius: '0.5rem',
                  background: selectedLevel === level ? '#667eea' : 'white',
                  color: selectedLevel === level ? 'white' : '#333',
                  cursor: 'pointer',
                  fontWeight: selectedLevel === level ? 'bold' : 'normal'
                }}
              >
                {level}
              </button>
            ))}
          </div>
        </div>

        {/* Step 2: 과목 선택 */}
        {selectedLevel && (
          <div style={{ marginBottom: '2rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
              과목:
            </label>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              {subjects[selectedLevel].map(subject => (
                <button
                  key={subject}
                  onClick={() => setSelectedSubject(subject)}
                  style={{
                    padding: '0.75rem 1.5rem',
                    border: selectedSubject === subject ? '2px solid #764ba2' : '2px solid #ddd',
                    borderRadius: '0.5rem',
                    background: selectedSubject === subject ? '#764ba2' : 'white',
                    color: selectedSubject === subject ? 'white' : '#333',
                    cursor: 'pointer',
                    fontWeight: selectedSubject === subject ? 'bold' : 'normal'
                  }}
                >
                  {subject}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 3: 이미지 선택 */}
        <div style={{ marginBottom: '2rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
            문제 사진:
          </label>
          <input
            type="file"
            accept="image/*"
            capture="environment"
            onChange={handleFileSelect}
            style={{
              display: 'block',
              width: '100%',
              padding: '0.75rem',
              border: '2px dashed #ddd',
              borderRadius: '0.5rem',
              cursor: 'pointer'
            }}
          />
          {capturedImage && (
            <div style={{ marginTop: '1rem', textAlign: 'center' }}>
              <img
                src={capturedImage}
                alt="촬영된 이미지"
                style={{
                  maxWidth: '100%',
                  maxHeight: '400px',
                  borderRadius: '0.5rem',
                  boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
                }}
              />
            </div>
          )}
        </div>

        {/* Step 4: 분석 버튼 */}
        {capturedImage && selectedLevel && selectedSubject && !analysisResult && (
          <button
            onClick={handleAnalyze}
            disabled={analyzing}
            style={{
              width: '100%',
              padding: '1rem',
              background: analyzing ? '#ccc' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '0.5rem',
              fontSize: '1.2rem',
              fontWeight: 'bold',
              cursor: analyzing ? 'not-allowed' : 'pointer',
              marginBottom: '1rem'
            }}
          >
            {analyzing ? '🤖 AI 분석 중...' : '🔍 AI 분석 시작'}
          </button>
        )}

        {/* 에러 메시지 */}
        {error && (
          <div style={{
            padding: '1rem',
            background: '#fee',
            border: '1px solid #fcc',
            borderRadius: '0.5rem',
            color: '#c33',
            marginBottom: '1rem'
          }}>
            ⚠️ {error}
          </div>
        )}

        {/* 분석 결과 */}
        {analysisResult && (
          <div style={{
            padding: '1.5rem',
            background: '#f9f9f9',
            borderRadius: '0.5rem',
            border: '2px solid #667eea'
          }}>
            <h2 style={{ fontSize: '1.5rem', marginBottom: '1rem', color: '#667eea' }}>
              ✅ 분석 완료
            </h2>

            {analysisResult.problem_text && (
              <div style={{ marginBottom: '1rem' }}>
                <strong>📝 문제:</strong>
                <div style={{
                  padding: '1rem',
                  background: 'white',
                  borderRadius: '0.5rem',
                  marginTop: '0.5rem',
                  whiteSpace: 'pre-wrap'
                }}>
                  {analysisResult.problem_text}
                </div>
              </div>
            )}

            {analysisResult.student_answer && (
              <div style={{ marginBottom: '1rem' }}>
                <strong>✏️ 내 답:</strong>
                <div style={{
                  padding: '1rem',
                  background: '#ffe',
                  borderRadius: '0.5rem',
                  marginTop: '0.5rem'
                }}>
                  {analysisResult.student_answer}
                </div>
              </div>
            )}

            {analysisResult.correct_answer && (
              <div style={{ marginBottom: '1rem' }}>
                <strong>✓ 정답:</strong>
                <div style={{
                  padding: '1rem',
                  background: '#efe',
                  borderRadius: '0.5rem',
                  marginTop: '0.5rem'
                }}>
                  {analysisResult.correct_answer}
                </div>
              </div>
            )}

            {analysisResult.explanation && (
              <div style={{ marginBottom: '1rem' }}>
                <strong>💡 해설:</strong>
                <div style={{
                  padding: '1rem',
                  background: 'white',
                  borderRadius: '0.5rem',
                  marginTop: '0.5rem',
                  whiteSpace: 'pre-wrap'
                }}>
                  {analysisResult.explanation}
                </div>
              </div>
            )}

            {analysisResult.key_concept && (
              <div style={{ marginBottom: '1rem' }}>
                <strong>🎯 핵심 개념:</strong>
                <div style={{
                  padding: '1rem',
                  background: '#eef',
                  borderRadius: '0.5rem',
                  marginTop: '0.5rem'
                }}>
                  {analysisResult.key_concept}
                </div>
              </div>
            )}

            {analysisResult.difficulty && (
              <div style={{ marginBottom: '1rem' }}>
                <strong>📊 난이도:</strong> {analysisResult.difficulty}
              </div>
            )}

            <button
              onClick={handleSave}
              style={{
                width: '100%',
                padding: '1rem',
                background: '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '0.5rem',
                fontSize: '1.2rem',
                fontWeight: 'bold',
                cursor: 'pointer',
                marginTop: '1rem'
              }}
            >
              💾 오답노트에 저장
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
